
import Vue from 'vue';
import rootConfig from '@root/config/root.config';
import { Data as TagData } from '@root/modules/tags/types/tag';
import { TranslateResult } from 'vue-i18n';
import { faShareNodes } from '@delfi.lt/fa-kit/icons/modules/classic/light';

type ShareData = {
  title: string;
  text: string;
  url: string;
};

interface Props {
  tag: TagData;
}

interface Data {
  popup: {
    width: number;
    height: number;
  };
  share: ShareData;
}

interface Computed {
  facebookUrl: string;
  linkedInUrl: string;
  messengerUrl: string;
}

interface Methods {
  copyLink: (string: string) => boolean;
  openPopup: (url: string, type: string) => void;
  openPrint: () => void;
  sharePage: () => Promise<void>;
  showLayer: (text: TranslateResult | string) => boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    tag: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      popup: {
        width: 720,
        height: 550,
      },
      share: {
        title: this.tag.metaTitle || this.tag.name,
        text: '',
        url: '',
      },
      faShareNodes,
    };
  },
  computed: {
    facebookUrl() {
      return `${rootConfig.social_share.facebook}?u=${encodeURI(this.share.url)}&t=${encodeURI(this.share.title)}`;
    },
    linkedInUrl() {
      return `${rootConfig.social_share.linkedin}?mini=true&url=${encodeURI(this.share.url)}`;
    },
    messengerUrl() {
      return `${rootConfig.social_share.messenger}?app_id=1476286675946138&link=${encodeURI(this.share.url)}&redirect_uri=${encodeURI(this.share.url)}`;
    },
  },
  mounted() {
    this.share.url = window.location.href;
  },
  methods: {
    copyLink(string) {
      this.$event.send('link-click', { name: 'Copy', type: 'tag-page-social-top' });

      let textarea;
      let result;
      try {
        textarea = document.createElement('textarea');
        textarea.setAttribute('readonly', 'true');
        textarea.setAttribute('contenteditable', 'true');
        textarea.style.position = 'fixed'; // prevent scroll from jumping to the bottom when focus is set.
        textarea.value = string;

        document.body.appendChild(textarea);

        textarea.select();

        const range = document.createRange();
        range.selectNodeContents(textarea);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);

        textarea.setSelectionRange(0, textarea.value.length);
        result = document.execCommand('copy');
        this.showLayer(this.$t(`info_layer.url_copied`));
      } catch (err) {
        result = null;
      } finally {
        if (textarea) {
          document.body.removeChild(textarea);
        }
      }

      // manual copy fallback using prompt
      if (!result) {
        const isMac = navigator.platform.toUpperCase().includes('MAC');
        const copyHotkey = isMac ? '⌘C' : 'CTRL+C';
        result = prompt(`Press ${copyHotkey}`, string);
        if (!result) {
          return false;
        }
      }

      return true;
    },
    openPopup(url, type) {
      this.$event.send('link-click', { name: type, url, type: 'tag-page-social-top' });

      if (window && window.open) {
        window.open(
          url,
          'popup_window',
          `width=${this.popup.width},height=${this.popup.height},directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no`
        );
      }
    },
    openPrint() {
      this.$event.send('link-click', { name: 'Print', type: 'tag-page-social-top' });

      if (window && window.print) {
        window.print();
      }
    },
    async sharePage() {
      this.share.url = window.location.href;
      this.share.title = document.title;

      const { title, text, url } = this.share;
      const androidWebAppMobileAPI = (window as { AndroidWebAppMobileAPI?: { sharePage?: (url: string, title: string, text: string) => void } })
        .AndroidWebAppMobileAPI;

      if (window.navigator.share) {
        try {
          await window.navigator.share({ title, text, url });
        } catch (error) {
          if ((error as DOMException)?.name !== 'AbortError') {
            throw error;
          }
        }
        return;
      }

      if (androidWebAppMobileAPI && androidWebAppMobileAPI.sharePage) {
        androidWebAppMobileAPI.sharePage(url, title, text);
        return;
      }

      this.copyLink(url);
    },
    showLayer(text) {
      if (!text) {
        return false;
      }
      const $infoLayer = document.createElement('div');
      $infoLayer.innerHTML = '<span>' + text + '</span>';
      $infoLayer.classList.add('info-layer');
      $infoLayer.style.display = 'block';
      document.body.appendChild($infoLayer);
      setTimeout(function () {
        $infoLayer.style.display = 'none';
        $infoLayer.parentNode?.removeChild($infoLayer);
      }, 2000);

      return true;
    },
  },
});
